import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Box, ButtonBase, Divider, Stack, Typography } from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { DashboardBlock, DashboardGroupHeading } from '@retail/dashboard/components';
import { useDashboardTFunction } from '@retail/dashboard/i18n';
import { useInfiniteChangelogEntries } from '@retail/sanity/data-access';
import { useConsumerType, useSanityLanguageCode } from '@retail/sanity/hooks';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { ChangelogEntry } from '@retail/changelog/types';
import { ChangelogEntriesDialog, ChangelogTags } from '@retail/changelog/components';
import { useChangelogDropdownStore } from '@retail/changelog/containers';
import { TextButton } from '@shared/components';
import { TbStatusChange } from 'react-icons/tb';

export const LatestChangesBlock = () => {
  const t = useDashboardTFunction();
  const language = useSanityLanguageCode();
  const consumer = useConsumerType();
  const { appTenant } = useSelectedOrgUnit();
  const [selectedEntry, setSelectedEntry] = useState<ChangelogEntry | undefined>();
  const openChangelogDropdown = useChangelogDropdownStore((state) => state.open);

  const { data, status } = useInfiniteChangelogEntries({
    language,
    consumer,
    country: appTenant.countryCode,
    pageSize: 5,
  });

  const entries = useMemo(
    () => (data?.pages?.[0] ?? []).map((dto) => ChangelogEntry.fromDto(dto, true)),
    [data?.pages]
  );

  return (
    <Stack gap={1.5} sx={{ height: '100%' }}>
      <DashboardGroupHeading
        title={t('dashboard.latestChanges.title')}
        icon={<TbStatusChange style={{ transform: 'rotate(90deg) scaleX(-1)' }} fontSize={20} />}
        action={
          <TextButton disabled={!openChangelogDropdown} onClick={() => openChangelogDropdown?.()}>
            {t('dashboard.latestChanges.showAll')}
          </TextButton>
        }
      />
      <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex' }}>
        <DashboardBlock
          isLoading={status === 'loading'}
          isError={status === 'error'}
          isEmpty={entries.length === 0}
          paperProps={{ sx: { px: 0, py: 2 } }}
        >
          <Stack divider={<Divider sx={{ my: 0 }} />}>
            {entries.map((entry) => (
              <ButtonBase
                key={entry.id}
                onClick={() => setSelectedEntry(entry)}
                sx={{
                  justifyContent: 'space-between',
                  textAlign: 'left',
                  gap: 1,
                  px: 3.5,
                  py: 1,
                  borderRadius: 0,
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <Stack sx={{ minWidth: 0 }} gap={0.5}>
                  <Typography variant="body2" fontWeight="bold" noWrap>
                    {entry.title}
                  </Typography>
                  <Stack direction="row" alignItems="center" gap={1} sx={{ flexWrap: 'wrap' }}>
                    <ChangelogTags tags={entry.tags} />
                    <Typography variant="caption" color="text.secondary">
                      {/* @ts-ignore */}
                      {dayjs(entry.changeDate).fromNow()}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{ flexShrink: 0, color: 'primary.main' }}
                >
                  <Typography variant="caption">{t('dashboard.latestChanges.readMore')}</Typography>
                  <ChevronRightRoundedIcon fontSize="small" />
                </Stack>
              </ButtonBase>
            ))}
          </Stack>

          {selectedEntry && (
            <ChangelogEntriesDialog
              entries={[selectedEntry]}
              onClose={() => setSelectedEntry(undefined)}
            />
          )}
        </DashboardBlock>
      </Box>
    </Stack>
  );
};
